import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Eye, MoreVertical, Loader, X } from 'lucide-react';
import FilterIcon from '../assets/Group 1261153979.svg';
import UserPlusIcon from '../assets/Group 1261153973.svg';
import MultoGroupIcon from '../assets/multoGroup.svg';
import SingleAddIcon from '../assets/singleAdd.svg';
import ExpandableSearchBar from '../components/ExpandableSearchBar';
import BulkEmployeeUploadModal from '../components/BulkEmployeeUploadModal';
import { employeesApi, departmentsApi, teamsApi, designationsApi } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { message } from 'antd';
import Pagination from '../components/Pagination';

/**
 * EmployeesListTable
 * Screenshot-style Employees list page (table + tabs + search + pagination)
 * NOTE: Uses mock data for now (can be wired to APIs later).
 */
function EmployeesListTable() {
  const { currentTheme } = useTheme();
  const navigate = useNavigate();

  // ---- UI State ----
  // activeTab is currently UI-only (kept to match the screenshot tabs)
  const [activeTab, setActiveTab] = useState('employees');
  // searchTerm filters mock rows (name / empId / contact / email)
  const [searchTerm, setSearchTerm] = useState('');
  // pageSize controls the rows per page and the total pages count
  const [pageSize, setPageSize] = useState(50);
  // current pagination page (1-based)
  const [page, setPage] = useState(1);
  // expanded rows set (used by +/- button in the leftmost column)
  const [rowsExpanded, setRowsExpanded] = useState(() => new Set());
  // action menu open state (which row's action dropdown is open)
  const [openActionMenu, setOpenActionMenu] = useState(null);
  // bulk upload modal state
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [teamModalState, setTeamModalState] = useState({ isOpen: false, employee: null, newTeamId: '', isLoading: false });

  // ---- API State ----
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [teams, setTeams] = useState([]);

  // ---- Add Employee Modal State ----
  const [showAddModal, setShowAddModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingEmployeeId, setEditingEmployeeId] = useState(null);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [toastMessage, setToastMessage] = useState('');
  const [showSuccessToast, setShowSuccessToast] = useState(false);

  const [formData, setFormData] = useState({
    employeeId: '',
    name: '',
    contactNumber: '',
    email: '',
    gender: 'MALE',
    addressStreet: '',
    landmark: '',
    pincode: '',
    city: '',
    district: '',
    state: '',
    departmentId: '',
    team: '',
    designationId: '',
    employeeCode: '',
    agentId: '',
    password: ''
  });

  // ---- Fetch Employees from API ----
  useEffect(() => {
    fetchEmployees();
    fetchDropdownData();
  }, []);

  const fetchDropdownData = async () => {
    try {
      const [depts, desigs, tms] = await Promise.all([
        departmentsApi.getAllDepartments(),
        designationsApi.getAllDesignations(),
        teamsApi.getAllTeams()
      ]);
      setDepartments(depts || []);
      setDesignations(desigs || []);
      setTeams(tms || []);
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await employeesApi.getAllEmployees();
      console.log("Fetched employees:", data);

      // Transform API data to match table format
      const transformedData = Array.isArray(data) ? data.map(emp => ({
        id: emp.id || emp._id || emp.employeeId,
        name: emp.name || '',
        empId: emp.employeeId || emp.empId || '',
        contact: emp.contactNumber || emp.contact || emp.phone || '',
        email: emp.email || emp.emailId || '',
        department: emp.departmentName || emp.department || '',
        designation: emp.designationName || emp.designation || emp.role || '',
        team: emp.teamName || emp.team || '',
        teamLead: emp.teamLeadName || emp.teamLead || '',
        teamLeadEmpId: emp.teamLeadEmpId || emp.teamLeadId || '',
        password: emp.password || 'N/A',
        ipAddress: emp.ipAddress || emp.ip || 'N/A',
        employeeCode: emp.employeeCode || emp.code || `EMP-${emp.employeeId || ''}`,
        gender: emp.gender || 'N/A',
        address: emp.addressStreet || emp.address || '',
        city: emp.city || '',
        state: emp.state || '',
        pincode: emp.pincode || emp.pinCode || '',
        loginPermission: emp.loginPermission ? 'Yes' : 'No',
        maskNumber: emp.maskNumber ? 'Yes' : 'No',
        status: emp.status !== undefined ? emp.status : (emp.isActive !== undefined ? emp.isActive : (emp.active !== undefined ? emp.active : true)),
      })) : [];

      setEmployees(transformedData);
    } catch (err) {
      console.error('Error fetching employees:', err);
      setError(err.message || 'Failed to fetch employees');
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  // ---- Mock Data (fallback if API fails) ----
  // Keep fields aligned with the screenshot table columns.
  const MOCK_EMPLOYEES = useMemo(
    () => [
      {
        id: 'e1',
        name: 'Nitesh Singh',
        empId: '1239',
        contact: '7838082254',
        email: '12suraj1@aarogyaindia.com',
        department: 'Verification',
        designation: 'Verifier Executive',
        team: 'Team A',
        teamLead: 'Pawan Sharma',
        teamLeadEmpId: '1239',
        password: 'manish@123',
        ipAddress: '103.13.72.99.202.56.232.203',
        employeeCode: 'EMP-1239',
        gender: 'Male',
        address: 'E-23, Sector 12',
        city: 'Noida',
        state: 'Uttar Pradesh',
        pincode: '201301',
        loginPermission: 'Yes',
        maskNumber: 'Yes',
        status: true,
      },
      {
        id: 'e2',
        name: 'Arihant Sagar',
        empId: '1123',
        contact: '7838082254',
        email: '12suraj1@aarogyaindia.com',
        department: 'Verification',
        designation: 'Verifier Executive',
        team: 'Team A',
        teamLead: 'Pushpendra Sh...',
        teamLeadEmpId: '1123',
        password: 'manish@123',
        ipAddress: '103.13.72.99.202.56.232.203',
        employeeCode: 'EMP-1123',
        gender: 'Male',
        address: 'A-45, Sector 15',
        city: 'Delhi',
        state: 'Delhi',
        pincode: '110001',
        loginPermission: 'Yes',
        maskNumber: 'Yes',
        status: true,
      },
      {
        id: 'e3',
        name: 'Sumit Kuma',
        empId: '1234',
        contact: '7838082254',
        email: '12suraj1@aarogyaindia.com',
        department: 'Verification',
        designation: 'Verifier Executive',
        team: 'Team A',
        teamLead: 'Divyanshu Singh',
        teamLeadEmpId: '1234',
        password: 'manish@123',
        ipAddress: '103.13.72.99.202.56.232.203',
        employeeCode: 'EMP-1234',
        gender: 'Male',
        address: 'B-67, Sector 18',
        city: 'Gurgaon',
        state: 'Haryana',
        pincode: '122001',
        loginPermission: 'Yes',
        maskNumber: 'Yes',
        status: false,
      },
      {
        id: 'e4',
        name: 'Noor Mohd.',
        empId: '3912',
        contact: '7838082254',
        email: '12dharmendra@aarogyaindia.com',
        department: 'NDR',
        designation: 'NDR Executive',
        team: 'Team B',
        teamLead: 'Divyanshu Singh',
        teamLeadEmpId: '3912',
        password: 'manish@123',
        ipAddress: '103.13.72.99.202.56.232.203',
        employeeCode: 'EMP-3912',
        gender: 'Male',
        address: 'C-89, Sector 20',
        city: 'Ghaziabad',
        state: 'Uttar Pradesh',
        pincode: '201001',
        loginPermission: 'Yes',
        maskNumber: 'Yes',
        status: true,
      },
      {
        id: 'e5',
        name: 'Neelesh Kumar',
        empId: '5433',
        contact: '7838082254',
        email: '12suraj1@aarogyaindia.com',
        department: 'NDR',
        designation: 'NDR Executive',
        team: 'Team B',
        teamLead: 'Pawan Sharma',
        teamLeadEmpId: '5433',
        password: 'manish@123',
        ipAddress: '103.13.72.99.202.56.232.203',
        employeeCode: 'EMP-5433',
        gender: 'Male',
        address: 'E-23, Sector 12',
        city: 'Noida',
        state: 'Uttar Pradesh',
        pincode: '201301',
        loginPermission: 'Yes',
        maskNumber: 'Yes',
        status: true,
      },
      {
        id: 'e6',
        name: 'Pooja Sinha',
        empId: '9800',
        contact: '7838082254',
        email: '12suraj1@aarogyaindia.com',
        department: 'NDR',
        designation: 'NDR Executive',
        team: 'Team B',
        teamLead: 'Pawan Sharma',
        teamLeadEmpId: '9800',
        password: 'manish@123',
        ipAddress: '103.13.72.99.202.56.232.203',
        employeeCode: 'EMP-9800',
        gender: 'Male',
        address: 'E-23, Sector 12',
        city: 'Noida',
        state: 'Uttar Pradesh',
        pincode: '201301',
        loginPermission: 'Yes',
        maskNumber: 'Yes',
        status: true,
      },
      {
        id: 'e7',
        name: 'Vipin Kumar So...',
        empId: '7677',
        contact: '7838082254',
        email: '12dharmendra@aarogyaindia.com',
        department: 'Telesales',
        designation: 'Telesales Executive',
        team: 'Team D',
        teamLead: 'Pawan Sharma',
        teamLeadEmpId: '7677',
        password: 'manish@123',
        ipAddress: '103.13.72.99.202.56.232.203',
        employeeCode: 'EMP-7677',
        gender: 'Male',
        address: 'E-23, Sector 12',
        city: 'Noida',
        state: 'Uttar Pradesh',
        pincode: '201301',
        loginPermission: 'Yes',
        maskNumber: 'Yes',
        status: true,
      },
      {
        id: 'e8',
        name: 'Divya Sharma',
        empId: '1112',
        contact: '7838082254',
        email: '12suraj1@aarogyaindia.com',
        department: 'Telesales',
        designation: 'Telesales Executive',
        team: 'Team D',
        teamLead: 'Pawan Sharma',
        teamLeadEmpId: '1112',
        password: 'manish@123',
        ipAddress: '103.13.72.99.202.56.232.203',
        employeeCode: 'EMP-1112',
        gender: 'Male',
        address: 'E-23, Sector 12',
        city: 'Noida',
        state: 'Uttar Pradesh',
        pincode: '201301',
        loginPermission: 'Yes',
        maskNumber: 'Yes',
        status: true,
      },
      {
        id: 'e9',
        name: 'Sanjay Gola',
        empId: '0987',
        contact: '7838082254',
        email: 'suraj1@aarogyaindia.com',
        department: 'Telesales',
        designation: 'Telesales Executive',
        team: 'Team D',
        teamLead: 'Anup Mishra',
        teamLeadEmpId: '0987',
        password: 'manish@123',
        ipAddress: '103.13.72.99.202.56.232.203',
        employeeCode: 'EMP-0987',
        gender: 'Male',
        address: 'E-23, Sector 12',
        city: 'Noida',
        state: 'Uttar Pradesh',
        pincode: '201301',
        loginPermission: 'Yes',
        maskNumber: 'Yes',
        status: true,
      },
      {
        id: 'e10',
        name: 'Noor Mohd.',
        empId: '3912',
        contact: '7838082254',
        email: '12dharmendra@aarogyaindia.com',
        department: 'Telesales',
        designation: 'Telesales Executive',
        team: 'Team D',
        teamLead: 'Anup Mishra',
        teamLeadEmpId: '3912',
        password: 'manish@123',
        ipAddress: '103.13.72.99.202.56.232.203',
        employeeCode: 'EMP-3912',
        gender: 'Male',
        address: 'E-23, Sector 12',
        city: 'Noida',
        state: 'Uttar Pradesh',
        pincode: '201301',
        loginPermission: 'Yes',
        maskNumber: 'Yes',
        status: true,
      },
      {
        id: 'e11',
        name: 'Rajesh Kumar',
        empId: '2345',
        contact: '9876543210',
        email: 'rajesh.kumar@aarogyaindia.com',
        department: 'Verification',
        designation: 'Verifier Executive',
        team: 'Team A',
        teamLead: 'Pawan Sharma',
        teamLeadEmpId: '1239',
        password: 'rajesh@123',
        ipAddress: '103.13.72.99.202.56.232.203',
        employeeCode: 'EMP-2345',
        gender: 'Male',
        address: 'D-12, Sector 14',
        city: 'Noida',
        state: 'Uttar Pradesh',
        pincode: '201301',
        loginPermission: 'Yes',
        maskNumber: 'No',
        status: true,
      },
      {
        id: 'e12',
        name: 'Priya Sharma',
        empId: '3456',
        contact: '9876543211',
        email: 'priya.sharma@aarogyaindia.com',
        department: 'NDR',
        designation: 'NDR Executive',
        team: 'Team B',
        teamLead: 'Divyanshu Singh',
        teamLeadEmpId: '1234',
        password: 'priya@123',
        ipAddress: '103.13.72.99.202.56.232.203',
        employeeCode: 'EMP-3456',
        gender: 'Female',
        address: 'F-34, Sector 16',
        city: 'Delhi',
        state: 'Delhi',
        pincode: '110001',
        loginPermission: 'Yes',
        maskNumber: 'Yes',
        status: false,
      },
      {
        id: 'e13',
        name: 'Amit Verma',
        empId: '4567',
        contact: '9876543212',
        email: 'amit.verma@aarogyaindia.com',
        department: 'Telesales',
        designation: 'Telesales Executive',
        team: 'Team D',
        teamLead: 'Anup Mishra',
        teamLeadEmpId: '0987',
        password: 'amit@123',
        ipAddress: '103.13.72.99.202.56.232.203',
        employeeCode: 'EMP-4567',
        gender: 'Male',
        address: 'G-56, Sector 18',
        city: 'Gurgaon',
        state: 'Haryana',
        pincode: '122001',
        loginPermission: 'No',
        maskNumber: 'Yes',
        status: true,
      },
      {
        id: 'e14',
        name: 'Sneha Patel',
        empId: '5678',
        contact: '9876543213',
        email: 'sneha.patel@aarogyaindia.com',
        department: 'Verification',
        designation: 'Verifier Executive',
        team: 'Team A',
        teamLead: 'Pushpendra Sh...',
        teamLeadEmpId: '1123',
        password: 'sneha@123',
        ipAddress: '103.13.72.99.202.56.232.203',
        employeeCode: 'EMP-5678',
        gender: 'Female',
        address: 'H-78, Sector 20',
        city: 'Ghaziabad',
        state: 'Uttar Pradesh',
        pincode: '201001',
        loginPermission: 'Yes',
        maskNumber: 'Yes',
        status: true,
      },
      {
        id: 'e15',
        name: 'Vikram Singh',
        empId: '6789',
        contact: '9876543214',
        email: 'vikram.singh@aarogyaindia.com',
        department: 'NDR',
        designation: 'NDR Executive',
        team: 'Team B',
        teamLead: 'Pawan Sharma',
        teamLeadEmpId: '5433',
        password: 'vikram@123',
        ipAddress: '103.13.72.99.202.56.232.203',
        employeeCode: 'EMP-6789',
        gender: 'Male',
        address: 'I-90, Sector 22',
        city: 'Noida',
        state: 'Uttar Pradesh',
        pincode: '201301',
        loginPermission: 'Yes',
        maskNumber: 'No',
        status: true,
      },
      {
        id: 'e16',
        name: 'Anjali Mehta',
        empId: '7890',
        contact: '9876543215',
        email: 'anjali.mehta@aarogyaindia.com',
        department: 'Telesales',
        designation: 'Telesales Executive',
        team: 'Team D',
        teamLead: 'Pawan Sharma',
        teamLeadEmpId: '7677',
        password: 'anjali@123',
        ipAddress: '103.13.72.99.202.56.232.203',
        employeeCode: 'EMP-7890',
        gender: 'Female',
        address: 'J-11, Sector 24',
        city: 'Delhi',
        state: 'Delhi',
        pincode: '110001',
        loginPermission: 'Yes',
        maskNumber: 'Yes',
        status: false,
      },
      {
        id: 'e17',
        name: 'Rohit Gupta',
        empId: '8901',
        contact: '9876543216',
        email: 'rohit.gupta@aarogyaindia.com',
        department: 'Verification',
        designation: 'Verifier Executive',
        team: 'Team A',
        teamLead: 'Divyanshu Singh',
        teamLeadEmpId: '1234',
        password: 'rohit@123',
        ipAddress: '103.13.72.99.202.56.232.203',
        employeeCode: 'EMP-8901',
        gender: 'Male',
        address: 'K-22, Sector 26',
        city: 'Gurgaon',
        state: 'Haryana',
        pincode: '122001',
        loginPermission: 'No',
        maskNumber: 'No',
        status: true,
      },
      {
        id: 'e18',
        name: 'Kavita Reddy',
        empId: '9012',
        contact: '9876543217',
        email: 'kavita.reddy@aarogyaindia.com',
        department: 'NDR',
        designation: 'NDR Executive',
        team: 'Team B',
        teamLead: 'Pawan Sharma',
        teamLeadEmpId: '9800',
        password: 'kavita@123',
        ipAddress: '103.13.72.99.202.56.232.203',
        employeeCode: 'EMP-9012',
        gender: 'Female',
        address: 'L-33, Sector 28',
        city: 'Ghaziabad',
        state: 'Uttar Pradesh',
        pincode: '201001',
        loginPermission: 'Yes',
        maskNumber: 'Yes',
        status: true,
      },
      {
        id: 'e19',
        name: 'Manish Tiwari',
        empId: '0123',
        contact: '9876543218',
        email: 'manish.tiwari@aarogyaindia.com',
        department: 'Telesales',
        designation: 'Telesales Executive',
        team: 'Team D',
        teamLead: 'Anup Mishra',
        teamLeadEmpId: '3912',
        password: 'manish@123',
        ipAddress: '103.13.72.99.202.56.232.203',
        employeeCode: 'EMP-0123',
        gender: 'Male',
        address: 'M-44, Sector 30',
        city: 'Noida',
        state: 'Uttar Pradesh',
        pincode: '201301',
        loginPermission: 'Yes',
        maskNumber: 'Yes',
        status: true,
      },
      {
        id: 'e20',
        name: 'Deepika Joshi',
        empId: '1235',
        contact: '9876543219',
        email: 'deepika.joshi@aarogyaindia.com',
        department: 'Verification',
        designation: 'Verifier Executive',
        team: 'Team A',
        teamLead: 'Pawan Sharma',
        teamLeadEmpId: '1239',
        password: 'deepika@123',
        ipAddress: '103.13.72.99.202.56.232.203',
        employeeCode: 'EMP-1235',
        gender: 'Female',
        address: 'N-55, Sector 32',
        city: 'Delhi',
        state: 'Delhi',
        pincode: '110001',
        loginPermission: 'Yes',
        maskNumber: 'No',
        status: false,
      },
    ],
    []
  );

  // ---- Derived Data: Filtering ----
  // Use API data if available, otherwise fallback to mock data
  const employeesData = employees.length > 0 ? employees : MOCK_EMPLOYEES;

  // Search employees using API if search term exists, otherwise use local filtering
  const [searchResults, setSearchResults] = useState(null);

  useEffect(() => {
    const performSearch = async () => {
      if (searchTerm.trim() && searchTerm.trim().length > 0) {
        try {
          const results = await employeesApi.searchEmployees(searchTerm);
          const transformedResults = Array.isArray(results) ? results.map(emp => ({
            id: emp.id || emp._id || emp.employeeId,
            name: emp.name || '',
            empId: emp.employeeId || emp.empId || '',
            contact: emp.contactNumber || emp.contact || emp.phone || '',
            email: emp.email || emp.emailId || '',
            department: emp.departmentName || emp.department || '',
            designation: emp.designationName || emp.designation || emp.role || '',
            team: emp.teamName || emp.team || '',
            teamLead: emp.teamLeadName || emp.teamLead || '',
            teamLeadEmpId: emp.teamLeadEmpId || emp.teamLeadId || '',
            password: emp.password || 'N/A',
            ipAddress: emp.ipAddress || emp.ip || 'N/A',
            employeeCode: emp.employeeCode || emp.code || `EMP-${emp.employeeId || ''}`,
            gender: emp.gender || 'N/A',
            address: emp.addressStreet || emp.address || '',
            city: emp.city || '',
            state: emp.state || '',
            pincode: emp.pincode || emp.pinCode || '',
            loginPermission: emp.loginPermission ? 'Yes' : 'No',
            maskNumber: emp.maskNumber ? 'Yes' : 'No',
            status: emp.status !== undefined ? emp.status : (emp.isActive !== undefined ? emp.isActive : (emp.active !== undefined ? emp.active : true)),
          })) : [];
          setSearchResults(transformedResults);
        } catch (err) {
          console.error('Error searching employees:', err);
          setSearchResults(null);
        }
      } else {
        setSearchResults(null);
      }
    };

    const debounceTimer = setTimeout(performSearch, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  // Filter by search term across a few key fields.
  const filtered = useMemo(() => {
    const dataToFilter = searchResults !== null ? searchResults : employeesData;
    const q = searchTerm.trim().toLowerCase();
    if (!q) return dataToFilter;
    return dataToFilter.filter((e) => {
      return (
        (e.name || '').toLowerCase().includes(q) ||
        (e.empId || '').toLowerCase().includes(q) ||
        (e.contact || '').toLowerCase().includes(q) ||
        (e.email || '').toLowerCase().includes(q)
      );
    });
  }, [employeesData, searchResults, searchTerm]);

  // ---- Derived Data: Pagination ----
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  // If filtering reduces totalPages, keep the current page within bounds.
  const safePage = Math.min(page, totalPages);

  const pageRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, pageSize, safePage]);

  /**
   * Toggle Component
   * 
   * A custom toggle switch component (like iOS toggle)
   * Used for Status field in the table
   * 
   * @param {boolean} checked - Current toggle state (on/off)
   * @param {Function} onChange - Callback function when toggle is clicked
   * @param {string} ariaLabel - Accessibility label for screen readers
   */
  function Toggle({ checked, onChange, ariaLabel }) {
    return (
      <button
        type="button"
        aria-label={ariaLabel}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2  ${checked ? '' : 'bg-gray-300'
          }`}
        style={{ backgroundColor: checked ? currentTheme.color : '#D1D5DB', '--tw-ring-color': currentTheme.color }}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-5' : 'translate-x-1'
            }`}
        />
      </button>
    );
  }

  /**
   * Toggle row expansion for the "expand" (+/-) button.
   * This is UI-only for now (shows a small detail panel under the row).
   */
  const toggleExpand = (rowId) => {
    setRowsExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  /**
   * Simple placeholder pages for other tabs (Department/Designation/Teams)
   * Requirement: when navigating via the tabs, show that page with an H1 tag.
   */
  const renderTabPage = () => {
    if (activeTab === 'department') return <h1 className="text-2xl font-bold text-gray-800">Department</h1>;
    if (activeTab === 'designation') return <h1 className="text-2xl font-bold text-gray-800">Designation</h1>;
    if (activeTab === 'teams') return <h1 className="text-2xl font-bold text-gray-800">Teams</h1>;
    return null;
  };

  /**
   * Update employee status (toggle on/off)
   */
  const updateEmployeeStatus = async (employeeId, newStatus) => {
    try {
      // Update employee status via API using PUT /employees/:id with { active: status }
      await employeesApi.updateEmployee(employeeId, { active: newStatus });
      showToast('Employee status updated successfully!');
      // Refresh employee list
      await fetchEmployees();
    } catch (err) {
      console.error('Error updating employee status:', err);
      showToast('Failed to update employee status', 'error');
      // Revert the change on error
      await fetchEmployees();
    }
  };

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (openActionMenu !== null) {
        setOpenActionMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openActionMenu]);

  const handleBulkUploadSuccess = async (result) => {
    console.log('Bulk upload completed:', result);
    // Refresh employee list after bulk upload
    await fetchEmployees();
  };

  // ---- Validation Functions ----
  const validateField = (name, value) => {
    let error = '';

    switch (name) {
      case 'employeeId':
        if (!value || value.trim() === '') {
          error = 'Employee ID is required';
        }
        break;
      case 'name':
        if (!value || value.trim() === '') {
          error = 'Employee name is required';
        } else if (value.length < 3) {
          error = 'Name must be at least 3 characters';
        }
        break;
      case 'contactNumber':
        if (!value || value.trim() === '') {
          error = 'Contact number is required';
        } else if (!/^[0-9]{10}$/.test(value)) {
          error = 'Contact number must be 10 digits';
        }
        break;
      case 'email':
        if (!value || value.trim() === '') {
          error = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = 'Please enter a valid email';
        }
        break;
      case 'addressStreet':
        if (!value || value.trim() === '') {
          error = 'Address is required';
        }
        break;
      case 'pincode':
        if (!value || value.trim() === '') {
          error = 'Pincode is required';
        } else if (!/^[0-9]{6}$/.test(value)) {
          error = 'Pincode must be 6 digits';
        }
        break;
      case 'city':
        if (!value || value.trim() === '') {
          error = 'City is required';
        }
        break;
      case 'district':
        if (!value || value.trim() === '') {
          error = 'District is required';
        }
        break;
      case 'state':
        if (!value || value.trim() === '') {
          error = 'State is required';
        }
        break;
      case 'departmentId':
        if (!value) {
          error = 'Department is required';
        }
        break;
      case 'team':
        if (!value) {
          error = 'Team is required';
        }
        break;
      case 'designationId':
        if (!value) {
          error = 'Designation is required';
        }
        break;
      case 'employeeCode':
        if (!value || value.trim() === '') {
          error = 'Employee code is required';
        }
        break;
      case 'agentId':
        if (!value || value.trim() === '') {
          error = 'Agent ID is required';
        }
        break;
      case 'password':
        if (!value || value.trim() === '') {
          error = 'Password is required';
        }
        break;
      default:
        break;
    }
    return error;
  };

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
      'employeeId', 'name', 'contactNumber', 'email', 'addressStreet',
      'pincode', 'city', 'district', 'state', 'departmentId',
      'team', 'designationId', 'employeeCode', 'agentId', 'password'
    ];
    requiredFields.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors({
        ...errors,
        [name]: error
      });
    }
  };

  const handleBlur = (name) => {
    setTouched({
      ...touched,
      [name]: true
    });
    const error = validateField(name, formData[name]);
    setErrors({
      ...errors,
      [name]: error
    });
  };

  const resetForm = () => {
    setFormData({
      employeeId: '',
      name: '',
      contactNumber: '',
      email: '',
      gender: 'MALE',
      addressStreet: '',
      landmark: '',
      pincode: '',
      city: '',
      district: '',
      state: '',
      departmentId: '',
      team: '',
      designationId: '',
      employeeCode: '',
      agentId: '',
      password: ''
    });
    setErrors({});
    setTouched({});
    setIsEditMode(false);
    setEditingEmployeeId(null);
  };

  const showToast = (msg, type = 'success') => {
    if (type === 'error') {
      message.error(msg);
    } else if (type === 'warning') {
      message.warning(msg);
    } else {
      message.success(msg);
    }
  };

  const handleEditEmployee = async (employeeObj) => {
    setOpenActionMenu(null);
    const empId = employeeObj.id || employeeObj._id || employeeObj.employeeId || employeeObj.empId;
    navigate(`/employees/edit/${empId}`);
  };

  const handleSaveEmployee = async () => {
    const allTouched = {};
    Object.keys(formData).forEach(key => {
      allTouched[key] = true;
    });
    setTouched(allTouched);

    if (!validateForm()) {
      showToast('Please fix all errors before submitting');
      return;
    }

    try {
      setLoading(true);
      const employeePayload = {
        employeeId: formData.employeeId,
        name: formData.name,
        email: formData.email,
        contactNumber: formData.contactNumber,
        gender: formData.gender,
        addressStreet: formData.addressStreet,
        landmark: formData.landmark,
        pincode: formData.pincode,
        city: formData.city,
        district: formData.district,
        state: formData.state,
        departmentId: parseInt(formData.departmentId),
        designationId: parseInt(formData.designationId),
        teamId: parseInt(formData.team),
        employeeCode: formData.employeeCode,
        agentId: formData.agentId,
        password: formData.password
      };

      if (isEditMode) {
        await employeesApi.updateEmployee(editingEmployeeId, employeePayload);
        showToast('Employee updated successfully!');
      } else {
        await employeesApi.createEmployee(employeePayload);
        showToast('Employee added successfully!');
      }

      setShowAddModal(false);
      resetForm();
      await fetchEmployees();
    } catch (error) {
      console.error('Error saving employee:', error);
      showToast(isEditMode ? 'Failed to update employee' : 'Failed to add employee', 'error');
    } finally {
      setLoading(false);
    }
  };

  const TeamChangeModal = () => {
    if (!teamModalState.isOpen) return null;

    const handleSubmit = async () => {
      if (!teamModalState.newTeamId) {
        showToast('Please enter a team ID', 'warning');
        return;
      }
      setTeamModalState(prev => ({ ...prev, isLoading: true }));
      try {
        await employeesApi.changeEmployeeTeam(teamModalState.employee.id, {
          newTeamId: parseInt(teamModalState.newTeamId),
          reason: 'Team reassignment'
        });
        await fetchEmployees();
        showToast('Team changed successfully!');
        setTeamModalState({ isOpen: false, employee: null, newTeamId: '', isLoading: false });
      } catch (err) {
        console.error('Error changing team:', err);
        showToast('Failed to change team', 'error');
        setTeamModalState(prev => ({ ...prev, isLoading: false }));
      }
    };

    return (
      <div className="fixed inset-0 bg-black/60 flex items-start pt-20 justify-center z-[9999] backdrop-blur-sm transition-all duration-300">
        <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl overflow-hidden">
          <div className="px-6 py-4 border-b flex items-center justify-between text-gray-800 bg-gray-50">
            <h3 className="text-lg font-bold flex items-center gap-2">Change Team</h3>
            <button disabled={teamModalState.isLoading} onClick={() => setTeamModalState({ ...teamModalState, isOpen: false })} className="text-gray-400 hover:text-gray-700 transition-colors p-1"><X className="w-5 h-5" /></button>
          </div>
          <div className="p-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">New Team <span className="text-red-500">*</span></label>
            <select value={teamModalState.newTeamId} onChange={e => setTeamModalState({ ...teamModalState, newTeamId: e.target.value })} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none bg-white">
              <option value="">Select a team</option>
              {teams.map(team => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
          </div>
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
            <button disabled={teamModalState.isLoading} onClick={() => setTeamModalState({ ...teamModalState, isOpen: false })} className="px-5 py-2.5 text-gray-600 font-medium hover:bg-gray-200 bg-gray-100 rounded-lg">Cancel</button>
            <button disabled={teamModalState.isLoading} onClick={handleSubmit} className="px-6 py-2.5 text-white font-medium bg-teal-500 hover:bg-teal-600 rounded-lg">{teamModalState.isLoading ? 'Processing...' : 'Change'}</button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Bulk Upload Modal */}

      {/* Bulk Upload Modal */}
      <BulkEmployeeUploadModal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onUploadSuccess={handleBulkUploadSuccess}
      />

      <TeamChangeModal />

      {/* Tabs (UI-only for now; kept to match screenshot layout) */}
      <div className="border-b border-gray-200">
        <div className="flex gap-8">
          {[
            { key: 'employees', label: 'Employees list' },
            { key: 'department', label: 'Department' },
            { key: 'designation', label: 'Designation' },
            { key: 'teams', label: 'Teams' },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveTab(t.key)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${activeTab === t.key
                ? 'text-gray-900 border-current'
                : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              style={{ borderColor: activeTab === t.key ? currentTheme.color : 'transparent', color: activeTab === t.key ? currentTheme.color : undefined }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Non-employees tabs: show dedicated page view with H1 */}
      {activeTab !== 'employees' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          {renderTabPage()}
        </div>
      )}

      {/* Employees list tab: show full table UI */}
      {activeTab === 'employees' && (
        <>
          {/* Loading State */}
          {loading && (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <div className="flex items-center justify-center space-x-3">
                <Loader className="w-6 h-6 animate-spin" style={{ color: currentTheme.color }} />
                <span className="text-gray-500">Loading employees...</span>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="bg-white rounded-xl border border-red-200 p-12 text-center">
              <div className="text-red-600 mb-4">Error: {error}</div>
              <button
                onClick={fetchEmployees}
                className="px-4 py-2 text-white rounded-lg transition-colors"
                style={{ backgroundColor: currentTheme.color }}
              >
                Retry
              </button>
            </div>
          )}

          {/* Search + actions row */}
          {!loading && !error && (
            <>
              <div className="flex items-center justify-between gap-4">
                {/* Left controls: search + page size */}
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-full max-w-sm">
                    <ExpandableSearchBar
                      searchTerm={searchTerm}
                      onSearchChange={(value) => {
                        setSearchTerm(value);
                        setPage(1);
                      }}
                      placeholder="Search by employee id or name"
                    />
                  </div>

                  {/* Page size selector */}
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(parseInt(e.target.value, 10));
                      setPage(1);
                    }}
                    className="h-[38px] px-4 border border-gray-300 rounded-full bg-white text-sm text-gray-700 focus:outline-none transition-all"
                    style={{ borderColor: pageSize ? currentTheme.color : '#D1D5DB' }}
                  >
                    <option value={10}>10 employees</option>
                    <option value={25}>25 employees</option>
                    <option value={50}>50 employees</option>
                    <option value={100}>100 employees</option>
                  </select>
                </div>

                {/* Right-side icon buttons (matches second image style) */}
                <div className="flex items-center gap-2">
                  {/* First Icon: White circle with light gray outline, light green filter icon */}
                  <button
                    type="button"
                    className="h-[38px] w-[38px] inline-flex items-center justify-center border border-gray-300 rounded-full bg-white shadow-sm hover:bg-gray-50 hover:shadow focus:outline-none focus:ring-2 focus:ring-offset-2"
                    title="Filter"
                    aria-label="Filter"
                    style={{ '--tw-ring-color': currentTheme.color }}
                  >
                    <img src={FilterIcon} alt="Filter" className="w-7 h-7" />
                  </button>

                  {/* Second Icon: Solid teal/light blue circle with person+plus icon */}
                  <button
                    type="button"
                    onClick={() => navigate('/employees/add')}
                    className="h-[38px] w-[38px] inline-flex items-center justify-center rounded-full shadow-sm hover:opacity-90 hover:shadow focus:outline-none focus:ring-2 focus:ring-offset-2"
                    title="Add Person"
                    aria-label="Add Person"
                    style={{ backgroundColor: currentTheme.color, '--tw-ring-color': currentTheme.color }}
                  >
                    <img src={SingleAddIcon} alt="Add Person" className="w-7 h-7" />
                  </button>

                  {/* Third Icon: Solid green circle with group+plus icon */}
                  <button
                    type="button"
                    onClick={() => setShowBulkUploadModal(true)}
                    className="h-[38px] w-[38px] inline-flex items-center justify-center rounded-full opacity-90 hover:opacity-100"
                    title="Add Group"
                    aria-label="Add Group"
                    style={{ backgroundColor: currentTheme.color }}
                  >
                    <img src={MultoGroupIcon} alt="Add Group" className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Employees table */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-[2200px] w-full">
                    <thead>
                      <tr className="text-white" style={{ background: currentTheme.color }}>
                        <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap rounded-tl-lg">
                          ALL
                        </th>
                        <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap">
                          EMPLOYEE
                        </th>
                        <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap">
                          EMPLOYEE CODE
                        </th>
                        <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap">
                          CONTACT
                        </th>
                        <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap">
                          EMAIL
                        </th>
                        <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap">
                          GENDER
                        </th>
                        <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap">
                          ADDRESS
                        </th>
                        <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap">
                          CITY
                        </th>
                        <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap">
                          STATE
                        </th>
                        <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap">
                          PINCODE
                        </th>
                        <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap">
                          DEPARTMENT
                        </th>
                        <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap">
                          DESIGNATION
                        </th>
                        <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap">
                          TEAM
                        </th>
                        <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap">
                          TEAM LEAD
                        </th>
                        <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap">
                          PASSWORD
                        </th>
                        <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap">
                          IP ADDRESS
                        </th>
                        <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap">
                          LOGIN PERMISSION
                        </th>
                        <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap">
                          MASK NUMBER
                        </th>
                        <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap">
                          STATUS
                        </th>
                        <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap rounded-tr-lg">
                          ACTION
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {pageRows.map((e, idx) => (
                        <React.Fragment key={e.id}>
                          {/* Main row */}
                          <tr className={idx % 2 === 0 ? 'bg-white' : 'bg-teal-50/30'}>
                            <td className="px-3 py-2">
                              <button
                                type="button"
                                onClick={() => toggleExpand(e.id)}
                                className="h-6 w-6 rounded-full border bg-white inline-flex items-center justify-center"
                                title="Expand"
                                aria-label="Expand"
                                style={{ borderColor: currentTheme.color, color: currentTheme.color }}
                              >
                                {rowsExpanded.has(e.id) ? '–' : '+'}
                              </button>
                            </td>
                            <td className="px-3 py-2">
                              <div className="text-sm font-medium text-gray-900 whitespace-nowrap leading-tight">{e.name}</div>
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap leading-tight">{e.employeeCode || 'N/A'}</td>
                            <td className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap leading-tight">{e.contact}</td>
                            <td className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap leading-tight">{e.email}</td>
                            <td className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap leading-tight">{e.gender || 'N/A'}</td>
                            <td className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap leading-tight">{e.address || 'N/A'}</td>
                            <td className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap leading-tight">{e.city || 'N/A'}</td>
                            <td className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap leading-tight">{e.state || 'N/A'}</td>
                            <td className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap leading-tight">{e.pincode || 'N/A'}</td>
                            <td className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap leading-tight">{e.department}</td>
                            <td className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap leading-tight">{e.designation}</td>
                            <td className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap leading-tight">{e.team}</td>
                            <td className="px-3 py-2">
                              <div className="leading-tight">
                                <div className="text-sm text-gray-800 whitespace-nowrap">{e.teamLead}</div>
                                <div className="text-[11px] text-gray-500">Emp : {e.teamLeadEmpId}</div>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap leading-tight">{e.password}</td>
                            <td className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap leading-tight">{e.ipAddress}</td>
                            <td className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap leading-tight">{e.loginPermission}</td>
                            <td className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap leading-tight">{e.maskNumber}</td>
                            <td className="px-3 py-2">
                              <Toggle
                                checked={e.status}
                                onChange={(checked) => updateEmployeeStatus(e.id, checked)}
                                ariaLabel={`Toggle status for ${e.name}`}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <div className="relative">
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setOpenActionMenu(openActionMenu === e.id ? null : e.id);
                                  }}
                                  className="inline-flex items-center justify-center p-1.5 rounded hover:bg-gray-100 transition-colors"
                                  aria-label="Actions"
                                  title="Actions"
                                >
                                  <Eye className="w-5 h-5 text-gray-600" />
                                </button>
                                {openActionMenu === e.id && (
                                  <div
                                    className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-2 w-40 z-20"
                                    onClick={(event) => event.stopPropagation()}
                                  >
                                    <button
                                      onClick={() => {
                                        navigate(`/employees/${e.id || e.empId}`);
                                        setOpenActionMenu(null);
                                      }}
                                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"
                                    >
                                      <svg width="17" height="11" viewBox="0 0 17 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path fillRule="evenodd" clipRule="evenodd" d="M3.05225 1.55148C1.38269 2.66477 0.344462 4.17054 0 5.40971C0.344406 6.64895 1.38269 8.15478 3.05225 9.26794C6.15483 11.3366 10.5875 11.3366 13.6899 9.26794C15.3595 8.15465 16.3977 6.64813 16.7422 5.40971C16.3978 4.17047 15.3595 2.66464 13.6899 1.55148C10.5874 -0.517159 6.15465 -0.517159 3.05225 1.55148ZM10.0845 3.69653C11.031 4.64308 11.031 6.17729 10.0845 7.12382C8.56124 8.64703 5.94812 7.56215 5.94812 5.41057C5.94812 3.25749 8.56196 2.17335 10.0845 3.69653ZM8.37121 1.86824C11.5159 1.86824 13.101 5.68863 10.8753 7.91384C8.6495 10.1396 4.82966 8.55458 4.82966 5.40978C4.82966 3.45411 6.41549 1.86824 8.37121 1.86824Z" fill={currentTheme.color} />
                                      </svg>
                                      <span>Details</span>
                                    </button>
                                    <button
                                      onClick={() => {
                                        handleEditEmployee(e);
                                      }}
                                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"
                                    >
                                      <svg width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M3.13045 14.2213H11.3075C12.7577 14.2213 13.9379 13.143 13.9379 11.818V8.46048C13.9379 8.26246 13.7606 8.10043 13.5439 8.10043C13.3271 8.10043 13.1498 8.26246 13.1498 8.46048V11.818C13.1498 12.7451 12.3242 13.5012 11.3075 13.5012H3.13045C2.11374 13.5012 1.28815 12.7469 1.28815 11.818V4.34867C1.28815 3.42153 2.11374 2.66542 3.13045 2.66542H6.48993C6.70668 2.66542 6.88401 2.5034 6.88401 2.30537C6.88401 2.10734 6.70668 1.94531 6.48993 1.94531H3.13045C1.68025 1.94531 0.5 3.02367 0.5 4.34867V11.818C0.5 13.1448 1.68025 14.2213 3.13045 14.2213Z" fill={currentTheme.color} stroke={currentTheme.color} />
                                        <path d="M11.7891 2.01562L13.8796 3.95263L8.79157 8.53688L6.56991 8.40199L6.70098 6.59987L11.7891 2.01562Z" fill={currentTheme.color} />
                                        <path d="M12.7839 1.11865C13.1966 0.746778 13.8577 0.747822 14.2605 1.12099L14.8926 1.70668C15.2953 2.07985 15.2872 2.68382 14.8744 3.0557L14.3497 3.5284L12.2592 1.59135L12.7839 1.11865Z" fill={currentTheme.color} />
                                      </svg>
                                      <span>Edit</span>
                                    </button>
                                    <button
                                      onClick={() => {
                                        setTeamModalState({
                                          isOpen: true,
                                          employee: e,
                                          newTeamId: '',
                                          isLoading: false
                                        });
                                        setOpenActionMenu(null);
                                      }}
                                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"
                                    >
                                      <svg width="16" height="20" viewBox="0 0 16 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M7.41827 15.542C6.89909 15.5388 6.3754 15.4932 5.85494 15.4017C5.80321 15.9195 5.78644 16.45 5.80636 16.9939C4.29189 15.435 2.76442 13.8625 1.22281 12.2755L7.5185 10.5831C7.25589 11.0045 7.02169 11.4358 6.81703 11.8785C6.8518 11.8847 6.88723 11.8911 6.92227 11.8976C7.08555 11.9297 7.24891 11.9593 7.41236 11.9863C8.55749 12.1757 9.71415 12.2369 10.8334 12.1263C11.0443 12.1053 11.2533 12.0774 11.4602 12.0429C11.6673 12.0087 11.8728 11.9677 12.0757 11.9197C12.2931 11.8688 12.5064 11.81 12.7151 11.7426C13.3407 11.5407 13.9281 11.2652 14.4566 10.9067C14.6394 10.7845 14.8138 10.6525 14.9792 10.5108C15.1445 10.3693 15.3007 10.2174 15.4464 10.0554C15.5716 9.92878 15.6937 9.79943 15.8154 9.6696C15.7789 9.83108 15.7358 9.99084 15.6863 10.1482C15.6363 10.3051 15.5801 10.4604 15.518 10.6127C15.082 11.6702 14.3783 12.6117 13.4966 13.3935C13.3766 13.4981 13.2542 13.6002 13.1278 13.699C12.7496 13.9945 12.3426 14.2616 11.912 14.496C11.7269 14.5946 11.538 14.6867 11.3444 14.7722C10.377 15.1997 9.29934 15.4584 8.16845 15.5179C7.91974 15.5355 7.66997 15.544 7.41827 15.542Z" fill={currentTheme.color} />
                                        <path d="M7.74571 3.39834C8.26182 3.34191 8.78729 3.32715 9.31481 3.3583C9.30676 2.838 9.26251 2.30902 9.18029 1.77101C10.8637 3.14573 12.5616 4.53249 14.2752 5.93211L8.21537 8.33595C8.42787 7.88724 8.61102 7.4319 8.76351 6.96864C8.72826 6.96649 8.69233 6.96415 8.65678 6.96176C8.49089 6.94856 8.32521 6.93793 8.15974 6.92987C7.00043 6.87316 5.8444 6.94518 4.74523 7.18348C4.53818 7.22859 4.33376 7.28024 4.13219 7.33833C3.93035 7.39603 3.73094 7.46037 3.53489 7.53129C3.32471 7.6069 3.11959 7.68972 2.91999 7.78067C2.32171 8.05303 1.7698 8.39417 1.28594 8.81092C1.11842 8.95332 0.960341 9.10448 0.812307 9.26424C0.664353 9.42371 0.526585 9.59261 0.400491 9.77026C0.290621 9.9104 0.184178 10.0529 0.0781683 10.1959C0.0958701 10.0312 0.120362 9.86759 0.151445 9.70562C0.183174 9.54399 0.221138 9.38332 0.265356 9.22485C0.57712 8.12425 1.16801 7.10828 1.95421 6.23041C2.06134 6.11271 2.17121 5.9972 2.28546 5.88463C2.62727 5.5476 3.00092 5.23561 3.40175 4.95332C3.57431 4.83412 3.75134 4.72087 3.93391 4.61372C4.84583 4.07803 5.88667 3.69735 7.00325 3.50845C7.24829 3.46233 7.49544 3.42527 7.74571 3.39834Z" fill={currentTheme.color} />
                                      </svg>
                                      <span>Change team</span>
                                    </button>
                                    <button
                                      onClick={async () => {
                                        if (window.confirm('Are you sure you want to force log out this employee?')) {
                                          try {
                                            // Note: This API call might not exist, adjust based on your backend
                                            // For now, we'll just log it
                                            console.log('Force log out for employee:', e.id);
                                            message.success('Employee logged out successfully!');
                                          } catch (err) {
                                            console.error('Error forcing log out:', err);
                                            message.error('Failed to force log out');
                                          }
                                        }
                                        setOpenActionMenu(null);
                                      }}
                                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"
                                    >
                                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M6 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V3.33333C2 2.97971 2.14048 2.64057 2.39052 2.39052C2.64057 2.14048 2.97971 2 3.33333 2H6M10.6667 11.3333L14 8M14 8L10.6667 4.66667M14 8H6" stroke={currentTheme.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                      </svg>
                                      <span>Force log out</span>
                                    </button>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>

                          {/* Expanded detail row (UI-only) */}
                          {rowsExpanded.has(e.id) && (
                            <tr className={idx % 2 === 0 ? 'bg-white' : undefined} style={{ backgroundColor: idx % 2 !== 0 ? `${currentTheme.color}10` : undefined }}>
                              <td colSpan={20} className="px-6 py-3">
                                <div className="rounded-lg p-3 text-sm text-gray-700 border"
                                  style={{ backgroundColor: `${currentTheme.color}08`, borderColor: `${currentTheme.color}20` }}>
                                  <div className="grid grid-cols-3 gap-4">
                                    <div>
                                      <div className="text-[11px] text-gray-500">Employee</div>
                                      <div className="font-medium">{e.name}</div>
                                    </div>
                                    <div>
                                      <div className="text-[11px] text-gray-500">Team</div>
                                      <div className="font-medium">{e.team}</div>
                                    </div>
                                    <div>
                                      <div className="text-[11px] text-gray-500">Team Lead</div>
                                      <div className="font-medium">{e.teamLead}</div>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}

                      {/* Empty state */}
                      {pageRows.length === 0 && (
                        <tr>
                          <td colSpan={20} className="px-4 py-12 text-center text-gray-500">
                            No employees found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination footer */}
              <Pagination 
                currentPage={page}
                totalItems={total}
                itemsPerPage={pageSize}
                onPageChange={(p) => setPage(p)}
              />
            </>
          )}

        </>
      )}

      {/* Add Employee Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
          <div className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-white px-6 py-4 flex items-center justify-between border-b">
              <h2 className="text-xl font-bold text-gray-800">
                {isEditMode ? 'Edit Employee' : 'Add New Employee'}
              </h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {isEditMode && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-800">
                      <span className="font-semibold">Editing Employee:</span> {formData.name} (ID: {formData.employeeId})
                    </p>
                  </div>
                )}

                {/* Basic Details */}
                <div>
                  <h3 className="text-base font-bold mb-4 text-gray-800">Basic Details</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">
                        Employee ID <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="234564"
                        value={formData.employeeId}
                        onChange={(e) => handleInputChange('employeeId', e.target.value)}
                        onBlur={() => handleBlur('employeeId')}
                        disabled={isEditMode}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none transition-all ${isEditMode ? 'bg-gray-100 cursor-not-allowed' : ''
                          } ${errors.employeeId && touched.employeeId ? 'border-red-500' : 'border-gray-300'
                          }`}
                        style={{ borderColor: formData.employeeId && !errors.employeeId ? currentTheme.color : undefined }}
                      />
                      {errors.employeeId && touched.employeeId && (
                        <p className="text-red-500 text-xs mt-1">{errors.employeeId}</p>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">
                        Employee Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Veer Bhan Singh"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        onBlur={() => handleBlur('name')}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none transition-all ${errors.name && touched.name ? 'border-red-500' : 'border-gray-300'
                          }`}
                        style={{ borderColor: formData.name && !errors.name ? currentTheme.color : undefined }}
                      />
                      {errors.name && touched.name && (
                        <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">
                        Contact number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="9999999999"
                        value={formData.contactNumber}
                        onChange={(e) => handleInputChange('contactNumber', e.target.value.replace(/\D/g, '').slice(0, 10))}
                        onBlur={() => handleBlur('contactNumber')}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none transition-all ${errors.contactNumber && touched.contactNumber ? 'border-red-500' : 'border-gray-300'
                          }`}
                        style={{ borderColor: formData.contactNumber && !errors.contactNumber ? currentTheme.color : undefined }}
                      />
                      {errors.contactNumber && touched.contactNumber && (
                        <p className="text-red-500 text-xs mt-1">{errors.contactNumber}</p>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        placeholder="awesome123@yopmail.com"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        onBlur={() => handleBlur('email')}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none transition-all ${errors.email && touched.email ? 'border-red-500' : 'border-gray-300'
                          }`}
                        style={{ borderColor: formData.email && !errors.email ? currentTheme.color : undefined }}
                      />
                      {errors.email && touched.email && (
                        <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">Gender</label>
                      <div className="flex gap-4 pt-2">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name="gender"
                            value="MALE"
                            checked={formData.gender === 'MALE'}
                            onChange={(e) => handleInputChange('gender', e.target.value)}
                            className="focus:ring-offset-2"
                            style={{ accentColor: currentTheme.color }}
                          />
                          <span className="text-sm">Male</span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name="gender"
                            value="FEMALE"
                            checked={formData.gender === 'FEMALE'}
                            onChange={(e) => handleInputChange('gender', e.target.value)}
                            className="focus:ring-offset-2"
                            style={{ accentColor: currentTheme.color }}
                          />
                          <span className="text-sm">Female</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div>
                  <h3 className="text-base font-bold mb-4 text-gray-800">Address</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">
                        Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="E-23, Sector 12"
                        value={formData.addressStreet}
                        onChange={(e) => handleInputChange('addressStreet', e.target.value)}
                        onBlur={() => handleBlur('addressStreet')}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none transition-all ${errors.addressStreet && touched.addressStreet ? 'border-red-500' : 'border-gray-300'
                          }`}
                        style={{ borderColor: formData.addressStreet && !errors.addressStreet ? currentTheme.color : undefined }}
                      />
                      {errors.addressStreet && touched.addressStreet && (
                        <p className="text-red-500 text-xs mt-1">{errors.addressStreet}</p>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">Landmark</label>
                      <input
                        type="text"
                        placeholder="Near Metro Station"
                        value={formData.landmark}
                        onChange={(e) => handleInputChange('landmark', e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none transition-all"
                        style={{ borderColor: formData.landmark ? currentTheme.color : undefined }}
                      />
                    </div>

                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">
                        Area pincode <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="201301"
                        value={formData.pincode}
                        onChange={(e) => handleInputChange('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
                        onBlur={() => handleBlur('pincode')}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none transition-all ${errors.pincode && touched.pincode ? 'border-red-500' : 'border-gray-300'
                          }`}
                        style={{ borderColor: formData.pincode && !errors.pincode ? currentTheme.color : undefined }}
                      />
                      {errors.pincode && touched.pincode && (
                        <p className="text-red-500 text-xs mt-1">{errors.pincode}</p>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">
                        City <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Noida"
                        value={formData.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                        onBlur={() => handleBlur('city')}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none transition-all ${errors.city && touched.city ? 'border-red-500' : 'border-gray-300'
                          }`}
                        style={{ borderColor: formData.city && !errors.city ? currentTheme.color : undefined }}
                      />
                      {errors.city && touched.city && (
                        <p className="text-red-500 text-xs mt-1">{errors.city}</p>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">
                        District <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Gautam Buddha Nagar"
                        value={formData.district}
                        onChange={(e) => handleInputChange('district', e.target.value)}
                        onBlur={() => handleBlur('district')}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none transition-all ${errors.district && touched.district ? 'border-red-500' : 'border-gray-300'
                          }`}
                        style={{ borderColor: formData.district && !errors.district ? currentTheme.color : undefined }}
                      />
                      {errors.district && touched.district && (
                        <p className="text-red-500 text-xs mt-1">{errors.district}</p>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">
                        State <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Uttar Pradesh"
                        value={formData.state}
                        onChange={(e) => handleInputChange('state', e.target.value)}
                        onBlur={() => handleBlur('state')}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none transition-all ${errors.state && touched.state ? 'border-red-500' : 'border-gray-300'
                          }`}
                        style={{ borderColor: formData.state && !errors.state ? currentTheme.color : undefined }}
                      />
                      {errors.state && touched.state && (
                        <p className="text-red-500 text-xs mt-1">{errors.state}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Employment details */}
                <div>
                  <h3 className="text-base font-bold mb-4 text-gray-800">Employment details</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">
                        Department <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.departmentId}
                        onChange={(e) => handleInputChange('departmentId', e.target.value)}
                        onBlur={() => handleBlur('departmentId')}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none transition-all appearance-none bg-white ${errors.departmentId && touched.departmentId ? 'border-red-500' : 'border-gray-300'
                          }`}
                        style={{ borderColor: formData.departmentId && !errors.departmentId ? currentTheme.color : undefined }}
                      >
                        <option value="">Select Department</option>
                        {departments.map(dept => (
                          <option key={dept.id} value={dept.id}>{dept.name}</option>
                        ))}
                      </select>
                      {errors.departmentId && touched.departmentId && (
                        <p className="text-red-500 text-xs mt-1">{errors.departmentId}</p>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">
                        Team <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.team}
                        onChange={(e) => handleInputChange('team', e.target.value)}
                        onBlur={() => handleBlur('team')}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none transition-all appearance-none bg-white ${errors.team && touched.team ? 'border-red-500' : 'border-gray-300'
                          }`}
                        style={{ borderColor: formData.team && !errors.team ? currentTheme.color : undefined }}
                      >
                        <option value="">Select Team</option>
                        {teams.map(team => (
                          <option key={team.id} value={team.id}>{team.name}</option>
                        ))}
                      </select>
                      {errors.team && touched.team && (
                        <p className="text-red-500 text-xs mt-1">{errors.team}</p>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">
                        Designation <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.designationId}
                        onChange={(e) => handleInputChange('designationId', e.target.value)}
                        onBlur={() => handleBlur('designationId')}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none transition-all appearance-none bg-white ${errors.designationId && touched.designationId ? 'border-red-500' : 'border-gray-300'
                          }`}
                        style={{ borderColor: formData.designationId && !errors.designationId ? currentTheme.color : undefined }}
                      >
                        <option value="">Select Designation</option>
                        {designations.map(designation => (
                          <option key={designation.id} value={designation.id}>{designation.name}</option>
                        ))}
                      </select>
                      {errors.designationId && touched.designationId && (
                        <p className="text-red-500 text-xs mt-1">{errors.designationId}</p>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">
                        Employee Code <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Emp-123911"
                        value={formData.employeeCode}
                        onChange={(e) => handleInputChange('employeeCode', e.target.value)}
                        onBlur={() => handleBlur('employeeCode')}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none transition-all ${errors.employeeCode && touched.employeeCode ? 'border-red-500' : 'border-gray-300'
                          }`}
                        style={{ borderColor: formData.employeeCode && !errors.employeeCode ? currentTheme.color : undefined }}
                      />
                      {errors.employeeCode && touched.employeeCode && (
                        <p className="text-red-500 text-xs mt-1">{errors.employeeCode}</p>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">
                        Agent ID <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="423"
                        value={formData.agentId}
                        onChange={(e) => handleInputChange('agentId', e.target.value)}
                        onBlur={() => handleBlur('agentId')}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none transition-all ${errors.agentId && touched.agentId ? 'border-red-500' : 'border-gray-300'
                          }`}
                        style={{ borderColor: formData.agentId && !errors.agentId ? currentTheme.color : undefined }}
                      />
                      {errors.agentId && touched.agentId && (
                        <p className="text-red-500 text-xs mt-1">{errors.agentId}</p>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">
                        Password <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Enter password"
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        onBlur={() => handleBlur('password')}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none transition-all ${errors.password && touched.password ? 'border-red-500' : 'border-gray-300'
                          }`}
                        style={{ borderColor: formData.password && !errors.password ? currentTheme.color : undefined }}
                      />
                      {errors.password && touched.password && (
                        <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Fixed Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEmployee}
                disabled={loading}
                className="px-8 py-2 text-white font-medium rounded-lg transition-colors flex items-center space-x-2"
                style={{ backgroundColor: currentTheme.color }}
              >
                {loading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <span>{isEditMode ? 'Update Employee' : 'Add Employee'}</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmployeesListTable;

